import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, Calendar, Clock, Share2, Tag } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { findPost, formatDate, relatedPosts } from "@/lib/blog-data";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = findPost(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData }) => {
    const post = loaderData?.post;
    if (!post) return {};
    const title = post.seoTitle ?? `${post.title} — Healic Blog`;
    const description = post.seoDescription ?? post.excerpt;
    const socialTitle = post.socialTitle ?? post.seoTitle ?? post.title;
    const socialDescription = post.socialDescription ?? post.seoDescription ?? post.excerpt;
    const url = `https://healic.in/blog/${post.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: socialTitle },
        { property: "og:description", content: socialDescription },
        { property: "og:url", content: url },
        { property: "og:image", content: post.cover },
        { property: "og:image:alt", content: post.title },
        { property: "og:type", content: "article" },
        { property: "article:published_time", content: post.date },
        { property: "article:author", content: post.author },
        { property: "article:section", content: post.category },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: socialTitle },
        { name: "twitter:description", content: socialDescription },
        { name: "twitter:image", content: post.cover },
        { name: "twitter:image:alt", content: post.title },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            image: [post.cover.startsWith("http") ? post.cover : `https://healic.in${post.cover}`],
            datePublished: post.date,
            dateModified: post.date,
            author: { "@type": "Person", name: post.author, jobTitle: post.authorRole },
            publisher: {
              "@type": "MedicalOrganization",
              name: "Healic Multispeciality Hospital",
              logo: { "@type": "ImageObject", url: "https://healic.in/favicon.png" },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
            articleSection: post.category,
            keywords: (post.tags ?? []).join(", "),
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Article not found</h1>
        <p className="mt-3 text-muted-foreground">
          That post may have been moved. Browse all articles instead.
        </p>
        <Link
          to="/blog"
          className="mt-6 inline-flex items-center gap-2 rounded-pill bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to blog
        </Link>
      </div>
    </PageShell>
  ),
  errorComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Something went wrong</h1>
      </div>
    </PageShell>
  ),
  component: PostPage,
});

function renderBody(body: string) {
  return body.split(/\n\n+/).map((block, i) => {
    if (block.startsWith("## ")) {
      return (
        <h2 key={i} className="mt-10 font-display text-2xl font-bold">
          {block.replace(/^##\s+/, "")}
        </h2>
      );
    }
    if (/^[-*]\s+/m.test(block)) {
      const items = block.split(/\n/).map((l) => l.replace(/^[-*]\s+/, ""));
      return (
        <ul key={i} className="mt-4 list-disc space-y-2 pl-6 text-base leading-relaxed">
          {items.map((it, j) => (
            <li key={j} dangerouslySetInnerHTML={{ __html: inline(it) }} />
          ))}
        </ul>
      );
    }
    if (/^\d+\.\s+/m.test(block)) {
      const items = block.split(/\n/).map((l) => l.replace(/^\d+\.\s+/, ""));
      return (
        <ol key={i} className="mt-4 list-decimal space-y-2 pl-6 text-base leading-relaxed">
          {items.map((it, j) => (
            <li key={j} dangerouslySetInnerHTML={{ __html: inline(it) }} />
          ))}
        </ol>
      );
    }
    return (
      <p
        key={i}
        className="mt-5 text-base leading-relaxed text-foreground/90"
        dangerouslySetInnerHTML={{ __html: inline(block) }}
      />
    );
  });
}

function inline(s: string) {
  return s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
}

function PostPage() {
  const { post } = Route.useLoaderData();
  const related = relatedPosts(post.slug, 3);

  return (
    <PageShell>
      <article className="bg-background">
        {/* Cover */}
        <div className="relative aspect-[21/7] max-h-[320px] w-full overflow-hidden bg-primary sm:aspect-[21/6]">
          <img src={post.cover} alt={post.title} fetchPriority="high" decoding="async" width={1200} height={630} className="h-full w-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
        </div>

        <div className="mx-auto -mt-10 max-w-3xl px-4 lg:px-0">
          <div className="rounded-2xl border border-border bg-card p-5 shadow-card sm:p-7">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Link
                to="/blog"
                className="inline-flex items-center gap-1 rounded-pill bg-cream px-2.5 py-1 font-semibold text-foreground hover:bg-accent/15"
              >
                <ArrowLeft className="h-3 w-3" /> Blog
              </Link>
              <span className="rounded-pill bg-accent/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-accent">
                {post.category}
              </span>
            </div>

            <h1 className="mt-4 font-display text-2xl font-bold leading-tight sm:text-3xl">
              {post.title}
            </h1>
            <p className="mt-2 text-base text-muted-foreground">{post.excerpt}</p>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-border pt-4 text-sm text-muted-foreground">
              <div>
                <p className="font-semibold text-foreground">{post.author}</p>
                <p className="text-xs">{post.authorRole}</p>
              </div>
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" /> {formatDate(post.date)}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" /> {post.readMinutes} min read
              </span>
              <button
                type="button"
                onClick={() => {
                  if (typeof window === "undefined") return;
                  const nav = window.navigator as Navigator & {
                    share?: (d: { title: string; url: string }) => Promise<void>;
                  };
                  if (nav.share) {
                    nav.share({ title: post.title, url: window.location.href }).catch(() => {});
                  } else {
                    nav.clipboard?.writeText(window.location.href);
                  }
                }}
                className="ml-auto inline-flex items-center gap-1.5 rounded-pill bg-cream px-3 py-1.5 text-xs font-semibold hover:bg-accent/15"
              >
                <Share2 className="h-3.5 w-3.5" /> Share
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="prose-healic mx-auto mt-6 max-w-2xl pb-8">{renderBody(post.body)}</div>

          {/* Tags */}
          <div className="mx-auto max-w-2xl pb-10">
            <div className="flex flex-wrap gap-1.5">
              {post.tags.map((t: string) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 rounded-pill bg-cream px-2.5 py-1 text-[11px] font-semibold text-foreground"
                >
                  <Tag className="h-3 w-3" /> {t}
                </span>
              ))}
            </div>
          </div>
        </div>


        {/* Related */}
        {related.length > 0 && (
          <section className="bg-muted/40 py-10 lg:py-12">
            <div className="mx-auto max-w-7xl px-4 lg:px-8">
              <h2 className="font-display text-xl font-bold sm:text-2xl">Continue reading</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to="/blog/$slug"
                    params={{ slug: r.slug }}
                    className="group overflow-hidden rounded-2xl border border-border bg-card transition hover:shadow-card"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={r.cover}
                        alt={r.title}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.05]"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-accent">
                        {r.category}
                      </p>
                      <p className="mt-1.5 font-display text-base font-semibold leading-snug group-hover:text-accent">
                        {r.title}
                      </p>
                      <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">{r.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

      </article>
    </PageShell>
  );
}
